import { IChartSettings, IParsedChartDataRecord } from '../interfaces';
import { IFunnelDataKeysPerLine } from './dataKeysUtils';
import { IFunnelLabelBar } from './types';
import { assertNotNil } from '../../../utils/types';
import React from 'react';
import { MaybeNil } from '../../../utils/interfaces';
import { CustomFunnelLabel } from './CustomFunnelLabel';
import { z } from 'zod';

export const generateBarId = (lineKey: string, isBounce: boolean): string => {
  return JSON.stringify({ l: lineKey, b: isBounce });
};

const parseBarId = (
  name: MaybeNil<string>,
): { lineKey: string; isBounce: boolean } => {
  assertNotNil(name);
  const nameParsed = JSON.parse(name);
  return {
    lineKey: nameParsed.l,
    isBounce: nameParsed.b,
  };
};

/**
 * Recharts' Customized component props are untyped (any). This is a minimal
 * schema that allows us to extract all bars on the chart with their dimensions.
 * It is derived by analysing the runtime structure of these props
 */
const rechartsCustomizedPropsSchema = z.object({
  formattedGraphicalItems: z
    .array(
      z.object({
        item: z
          .object({
            props: z
              .object({
                id: z.string().nullish(),
                fill: z.string().nullish(),
              })
              .nullish(),
          })
          .nullish(),
        props: z
          .object({
            data: z
              .array(
                z.object({
                  height: z.number().nullish(),
                  width: z.number().nullish(),
                  x: z.number().nullish(),
                  y: z.number().nullish(),
                }),
              )
              .nullish(),
          })
          .nullish(),
      }),
    )
    .nullable(),
});

interface IProps {
  rechartsProps?: unknown;
  data: IParsedChartDataRecord[];
  dataKeysPerLine: IFunnelDataKeysPerLine;
  chartSettings: IChartSettings;
}

export const CustomFunnelLabels = ({
  rechartsProps: rechartsPropsAsUnknown,
  data,
  dataKeysPerLine,
  chartSettings,
}: IProps) => {
  const parsedRechartsCustomizedProps = rechartsCustomizedPropsSchema.safeParse(
    rechartsPropsAsUnknown,
  );
  if (!parsedRechartsCustomizedProps.success) {
    console.warn(
      "Recharts' Customized component props are not in the expected format. Review the " +
        'structure and update rechartsCustomizedPropsSchema if necessary',
      {
        parseError: parsedRechartsCustomizedProps.error,
        rechartsProp: rechartsPropsAsUnknown,
      },
    );
    return null;
  }
  const rechartsBarDef =
    parsedRechartsCustomizedProps.data.formattedGraphicalItems ?? [];
  const barsById: Record<
    string,
    {
      conversionBar?: IFunnelLabelBar;
      bounceBar?: IFunnelLabelBar;
    }
  > = {};

  rechartsBarDef.forEach((barDef) => {
    const column = parseBarId(barDef.item?.props?.id);
    const rechartsBars = barDef.props?.data ?? [];
    rechartsBars.forEach((bar, index) => {
      const barId = `${index.toString()}:${column.lineKey}`;
      if (!barsById.hasOwnProperty(barId)) {
        barsById[barId] = {};
      }

      const dataKeys = dataKeysPerLine[column.lineKey];
      assertNotNil(dataKeys);

      const labelPos: IFunnelLabelBar = {
        dataKeys,
        index,
        bar: {
          x: bar.x ?? 0,
          y: bar.y ?? 0,
          width: bar.width ?? 0,
          height: bar.height ?? 0,
        },
        color: barDef.item?.props?.fill,
      };
      if (column.isBounce) {
        barsById[barId].bounceBar = labelPos;
      } else {
        barsById[barId].conversionBar = labelPos;
      }
    });
  });

  return (
    <>
      {Object.entries(barsById).map(([key, lg]) => (
        <CustomFunnelLabel
          key={key}
          data={data}
          bounceBar={lg.bounceBar}
          conversionBar={lg.conversionBar}
          chartSettings={chartSettings}
        />
      ))}
    </>
  );
};
